/** Admin Stats dashboard — leaderboard, coverage, status pie, top viewed. */
import { useState, useEffect } from 'react';
import { api } from '../../auth/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

const COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#6b7280', '#ef4444'];

export default function AdminStats() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/wiki/dashboard/admin')
            .then(res => setData(res.data))
            .catch(e => setError('Failed to load admin stats'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="p-6 text-slate-400">Loading admin dashboard...</div>;
    if (error) return <div className="p-6 text-red-400">{error}</div>;
    if (!data) return null;

    const statusPie = Object.entries(data.status_distribution || {}).map(([name, value]) => ({ name, value }));

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <h1 className="text-xl font-bold text-white mb-6">Admin Dashboard</h1>

            {/* Leaderboard */}
            <div className="mb-8">
                <h2 className="text-lg font-semibold text-amber-400 mb-4">Contributor Leaderboard (30 days)</h2>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
                    <table className="w-full text-sm text-slate-300">
                        <thead className="bg-slate-700/50 text-slate-400">
                            <tr>
                                <th className="px-4 py-3 text-left">#</th>
                                <th className="px-4 py-3 text-left">Name</th>
                                <th className="px-4 py-3 text-left">Email</th>
                                <th className="px-4 py-3 text-left">Role</th>
                                <th className="px-4 py-3 text-right">Edits (30d)</th>
                                <th className="px-4 py-3 text-right">Total Edits</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.leaderboard.map((u, i) => (
                                <tr key={u.id} className="border-t border-slate-700/50">
                                    <td className="px-4 py-3">{i + 1}</td>
                                    <td className="px-4 py-3 font-medium text-white">{u.full_name}</td>
                                    <td className="px-4 py-3">{u.email}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded text-xs
                                            ${u.role === 'admin' ? 'bg-red-500/20 text-red-400' :
                                                u.role === 'editor' ? 'bg-blue-500/20 text-blue-400' :
                                                    'bg-slate-500/20 text-slate-400'}`}>
                                            {u.role}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right font-semibold text-amber-400">{u.edits_30d}</td>
                                    <td className="px-4 py-3 text-right">{u.total_edits}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Category Coverage */}
            <div className="mb-8">
                <h2 className="text-lg font-semibold text-amber-400 mb-4">Category Coverage</h2>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
                    <table className="w-full text-sm text-slate-300">
                        <thead className="bg-slate-700/50 text-slate-400">
                            <tr>
                                <th className="px-4 py-3 text-left">Category</th>
                                <th className="px-4 py-3 text-right">Published</th>
                                <th className="px-4 py-3 text-right">Drafts</th>
                                <th className="px-4 py-3 text-right">Total</th>
                                <th className="px-4 py-3 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.categories.map(c => (
                                <tr key={c.id} className="border-t border-slate-700/50">
                                    <td className="px-4 py-3 font-medium text-white">{c.name || c.name_vi}</td>
                                    <td className="px-4 py-3 text-right">{c.published_count}</td>
                                    <td className="px-4 py-3 text-right">{c.draft_count}</td>
                                    <td className="px-4 py-3 text-right">{c.total}</td>
                                    <td className="px-4 py-3 text-center">
                                        {c.needs_attention ? (
                                            <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">Needs Attention</span>
                                        ) : (
                                            <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">OK</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Status Pie Chart */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                    <h2 className="text-lg font-semibold text-amber-400 mb-4">Entry Status Distribution</h2>
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie data={statusPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                                {statusPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Top Viewed */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                    <h2 className="text-lg font-semibold text-amber-400 mb-4">Top Viewed (30 days)</h2>
                    <div className="space-y-2">
                        {data.top_viewed.map((e, i) => (
                            <div key={e.id} className="flex items-center gap-3 p-2 rounded bg-slate-700/30">
                                <span className="w-6 h-6 rounded-full text-sm flex items-center justify-center bg-amber-500/20 text-amber-400 font-bold text-xs">{i + 1}</span>
                                <a href={`/wiki/entry/${e.slug}`} className="flex-1 text-sm text-white hover:text-amber-400 truncate">
                                    {e.title_en || e.title_vi}
                                </a>
                                <span className="text-sm text-slate-400 font-mono">{e.view_count} views</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}